import React, { useCallback, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import { listClassRules, getClassCanonicalLevels, previewClassRuleRemoval, deactivateClassRule, reactivateClassRule, deleteClassRule } from '../services/classAuditApi';
import RuleEditorDialog from './RuleEditorDialog';

// Rule Management — the administrative home for REUSABLE rules (app.lksi_team_level_mapping_rules).
// Distinct from Class Audit: Class Audit is where recurring patterns are discovered and one-class
// overrides are made; here rules are viewed and maintained. Pending Rebuild is DERIVED state (the
// rule changed since the last warehouse build) — a saved rule is authoritative immediately, while
// Class Audit reflects the prior warehouse state until dbt runs. Baseline (migrated) rules are
// managed here too, distinguished only by a provenance badge and an extra deactivation confirmation.

const fmtDate = (x) => (x ? String(x).slice(0, 10) : '');
const scopeLabel = (comCode) => (comCode === '*' ? 'All franchises' : comCode);
const isBaseline = (r) => r.source === 'dbt_seed';

export default function RuleManagement() {
  const [rules, setRules] = useState([]);
  const [warehouse, setWarehouse] = useState(null);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // active | inactive | all
  const [editor, setEditor] = useState({ open: false, mode: 'create', rule: null });
  const [confirm, setConfirm] = useState(null); // { rule } pending deactivation
  // Removal impact for the rule in the confirm dialog: null while loading, then the preview counts
  // (or { failed: true } if the preview call errored -- deactivation stays available either way).
  const [removalImpact, setRemovalImpact] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { rule } pending permanent delete
  const [baselineAck, setBaselineAck] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    getClassCanonicalLevels()
      .then((res) => { if (!cancelled) setLevels(res.levels || []); })
      .catch(() => { if (!cancelled) setLevels([]); });
    return () => { cancelled = true; };
  }, []);

  const fetchRules = useCallback(() => {
    setLoading(true); setError('');
    const params = statusFilter === 'all' ? {} : { is_active: statusFilter === 'active' ? '1' : '0' };
    listClassRules(params)
      .then((res) => { setRules(res.rules || []); setWarehouse(res.warehouse || null); setLoading(false); })
      .catch((err) => { setError(err.message || 'Failed to load rules.'); setRules([]); setLoading(false); });
  }, [statusFilter]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const onSaved = () => { setNotice(''); fetchRules(); };

  // Opening the confirm dialog fetches the removal impact first, so the steward decides WITH the
  // consequences in front of them instead of reading them in the after-the-fact notice. The preview
  // is read-only and runs the same calculation the deactivation itself reports.
  const requestDeactivate = (rule) => {
    setBaselineAck(false); setConfirm({ rule }); setRemovalImpact(null);
    previewClassRuleRemoval(rule.rule_id)
      .then((res) => setRemovalImpact((res.preview && res.preview.counts) || { unavailable: true }))
      .catch(() => setRemovalImpact({ failed: true }));
  };

  const doDeactivate = () => {
    const rule = confirm.rule;
    setBusyId(rule.rule_id); setConfirm(null); setRemovalImpact(null); setError(''); setNotice('');
    deactivateClassRule(rule.rule_id)
      .then((res) => {
        setBusyId(null);
        const p = res.preview && res.preview.counts;
        // Ambiguity is reported whenever it is non-zero: it is the one outcome that can fail the
        // nightly warehouse integrity check, so it must never be dropped from the summary.
        setNotice(p
          ? `Rule deactivated — ${p.will_unmap} class(es) now unassigned, ${p.will_change} changed`
            + (p.ambiguous ? `, ${p.ambiguous} left with an ambiguous rule match` : '')
            + '. Pending warehouse rebuild.'
          : 'Rule deactivated. Pending warehouse rebuild.');
        fetchRules();
      })
      .catch((err) => { setBusyId(null); setError(err.message || 'Deactivation failed.'); });
  };

  const doReactivate = (rule) => {
    setBusyId(rule.rule_id); setError(''); setNotice('');
    reactivateClassRule(rule.rule_id)
      .then(() => { setBusyId(null); setNotice('Rule reactivated. Pending warehouse rebuild.'); fetchRules(); })
      .catch((err) => { setBusyId(null); setError(err.message || 'Reactivation failed.'); });
  };

  const doDelete = () => {
    const rule = deleteConfirm.rule;
    setBusyId(rule.rule_id); setDeleteConfirm(null); setError(''); setNotice('');
    deleteClassRule(rule.rule_id)
      .then(() => {
        setBusyId(null);
        // Remove the row immediately (no rebuild needed — the warehouse already dropped it on deactivation).
        setRules((prev) => prev.filter((x) => x.rule_id !== rule.rule_id));
        setNotice(`Rule permanently deleted: ${rule.team_name_pattern} (${scopeLabel(rule.com_code)}).`);
      })
      .catch((err) => { setBusyId(null); setError(err.message || 'Delete failed.'); });
  };

  // ── rows (house rule: extract .map() out of JSX) ──
  const ruleRows = rules.map((r) => {
    const active = r.is_active === true || r.is_active === 1;
    const steward = r.updated_by ? `${r.updated_by} · ${fmtDate(r.updated_at)}` : `${r.created_by} · ${fmtDate(r.created_at)}`;
    return (
      <tr key={r.rule_id}>
        <td className="text-start">
          {r.team_name_pattern}
          {isBaseline(r) && <Chip size="small" variant="outlined" label="Baseline" sx={{ ml: 1, height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }} />}
        </td>
        <td className="text-start text-secondary">{scopeLabel(r.com_code)}</td>
        <td className="text-start">{r.canonical_level_name || `#${r.level_id}`}</td>
        <td className="text-start text-secondary">{r.priority}</td>
        <td className="text-start">
          <Chip size="small" variant="outlined" color={active ? 'success' : 'default'} label={active ? 'Active' : 'Inactive'} />
          {r.pending_rebuild === true && (
            <Chip size="small" color="warning" variant="outlined" label="Pending rebuild" sx={{ ml: 1 }}
              title="Saved in Season Manager; Class Audit reflects it after the next warehouse rebuild." />
          )}
        </td>
        {/* Steward identity is shown IN FULL -- no truncation, no substitute display name. Migration-
            authored values (system:2026-08-07_003_arenasports_si_class_level_rules) break at the
            date's hyphen but not at underscores, so the tail is one ~40-char unbreakable run that
            filled the cell to its right edge; with .table-sm's 4px padding as the only gutter it
            landed hard against the Actions buttons. overflowWrap 'anywhere' lets that run break
            (and, unlike 'break-word', lets the column shrink rather than forcing width), and pe-3
            gives a real gutter. Whether a migration identifier is the right thing to show a steward
            at all is a SEPARATE open product question -- this only stops the collision. */}
        <td className="text-start text-secondary pe-3" title={steward} style={{ overflowWrap: 'anywhere' }}>{steward}</td>
        <td className="text-start" style={{ whiteSpace: 'nowrap' }}>
          <Button size="small" onClick={() => setEditor({ open: true, mode: 'edit', rule: r })} disabled={busyId === r.rule_id}>Edit</Button>
          {active ? (
            <Button size="small" color="inherit" onClick={() => requestDeactivate(r)} disabled={busyId === r.rule_id}>Deactivate</Button>
          ) : (
            <>
              <Button size="small" color="inherit" onClick={() => doReactivate(r)} disabled={busyId === r.rule_id}>Reactivate</Button>
              {/* Delete is Season-Manager-only (baseline rules can be deactivated, never deleted) and stays
                  disabled until the deactivation has been consumed (pending_rebuild clears) so it's pure cleanup. */}
              {r.source === 'season_manager' && (
                <Button size="small" color="error"
                  onClick={() => setDeleteConfirm({ rule: r })}
                  disabled={busyId === r.rule_id || r.pending_rebuild === true}
                  title={r.pending_rebuild === true
                    ? 'Available after the warehouse consumes the deactivation (Pending rebuild clears).'
                    : undefined}>
                  Delete
                </Button>
              )}
            </>
          )}
          {busyId === r.rule_id && <CircularProgress size={14} sx={{ ml: 1 }} />}
        </td>
      </tr>
    );
  });

  const confirmingBaseline = confirm && isBaseline(confirm.rule);

  // ── removal-impact disclosure for the deactivate dialog (counts only, no affected-class list) ──
  // Ambiguity is separated out and rendered as a warning Alert rather than another number: falling
  // back to a tie between equally-ranked rules with different levels is an unresolved ambiguity,
  // and it fails the nightly warehouse integrity check until a rule or a manual assignment settles
  // it. The other outcomes are routine.
  let impactBlock = null;
  if (confirm && removalImpact === null) {
    impactBlock = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">Calculating impact…</Typography>
      </Box>
    );
  } else if (removalImpact && (removalImpact.failed || removalImpact.unavailable)) {
    impactBlock = (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Impact preview unavailable. You can still deactivate; the result is reported afterwards.
      </Typography>
    );
  } else if (removalImpact) {
    impactBlock = (
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2">
          {removalImpact.will_change} class{removalImpact.will_change === 1 ? '' : 'es'} fall back to another rule
          {' · '}{removalImpact.will_unmap} become unassigned
          {' · '}{removalImpact.no_change} unchanged
        </Typography>
        {removalImpact.shadowed_by_override > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {removalImpact.shadowed_by_override} of these keep their current level anyway — a manual
            assignment overrides the rule.
          </Typography>
        )}
        {removalImpact.ambiguous > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <b>{removalImpact.ambiguous} class{removalImpact.ambiguous === 1 ? '' : 'es'} would be left
            with an ambiguous rule match.</b> Two or more equally-ranked rules would disagree on the
            level, so the winner would be decided by a technical tie-break with no business meaning.
            This fails the nightly warehouse integrity check until a rule or a manual assignment
            resolves it.
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Container fluid>
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          View and maintain reusable mapping rules. A saved rule is authoritative immediately; Class Audit
          reflects it after the next warehouse rebuild.
        </Typography>
        {warehouse && warehouse.suggestions_built_at && (
          <Typography variant="caption" color="text.secondary">
            Warehouse last built: {fmtDate(warehouse.suggestions_built_at)}
          </Typography>
        )}
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup size="small" exclusive value={statusFilter} onChange={(e, v) => v && setStatusFilter(v)}>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" size="small" onClick={() => setEditor({ open: true, mode: 'create', rule: null })}>
          New rule
        </Button>
      </Paper>

      {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={20} /><Typography variant="body2" color="text.secondary">Loading rules…</Typography>
        </Box>
      ) : rules.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No rules match this filter.</Typography>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th className="text-start">Pattern</th>
                <th className="text-start">Scope</th>
                <th className="text-start">Level</th>
                <th className="text-start">Priority</th>
                <th className="text-start">Status</th>
                <th className="text-start">Last steward</th>
                <th className="text-start">Actions</th>
              </tr>
            </thead>
            <tbody>{ruleRows}</tbody>
          </table>
        </div>
      )}

      <RuleEditorDialog
        open={editor.open}
        mode={editor.mode}
        rule={editor.rule}
        levels={levels}
        onSaved={onSaved}
        onClose={() => setEditor({ open: false, mode: 'create', rule: null })}
      />

      <Dialog open={Boolean(confirm)} onClose={() => { setConfirm(null); setRemovalImpact(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Deactivate rule?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <code>{confirm && confirm.rule.team_name_pattern}</code> → {confirm && (confirm.rule.canonical_level_name || `#${confirm.rule.level_id}`)}
            {' '}({confirm && scopeLabel(confirm.rule.com_code)}).
          </Typography>
          {/* Deactivation is NOT simply "removes a mapping": each class this rule currently wins
              falls back to the next-ranked matching rule, becomes unassigned if none remains, or
              lands on a tie between equally-ranked rules. Name all three. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            On the next warehouse rebuild, every class this rule currently wins falls back to the
            next-ranked rule that matches it, becomes unassigned if none does, or is left ambiguous
            if the rules it falls back to disagree. You can reactivate it later.
          </Typography>

          {impactBlock}

          {confirmingBaseline && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              This is a migrated <b>baseline</b> rule. Deactivating it may un-map many classes across all franchises.
              <FormControlLabel sx={{ mt: 0.5, display: 'block' }}
                control={<Checkbox size="small" checked={baselineAck} onChange={(e) => setBaselineAck(e.target.checked)} />}
                label="I understand this is a baseline rule." />
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirm(null); setRemovalImpact(null); }}>Cancel</Button>
          <Button color="error" variant="contained" disabled={confirmingBaseline && !baselineAck} onClick={doDeactivate}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Permanently delete rule?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <code>{deleteConfirm && deleteConfirm.rule.team_name_pattern}</code> → {deleteConfirm && (deleteConfirm.rule.canonical_level_name || `#${deleteConfirm.rule.level_id}`)}
            {' '}({deleteConfirm && scopeLabel(deleteConfirm.rule.com_code)}).
          </Typography>
          <Alert severity="error">
            This permanently removes the rule <b>and its stewardship history</b>. It cannot be undone. The warehouse
            has already re-resolved the classes this rule used to win — without it — so no rebuild is needed.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doDelete}>Delete permanently</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
